import {useApolloClient} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {RealtimeMessageType} from 'realtime/Constants';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import {ChatHistoryItemIcon, ChatSidepanelItem} from '../ChatListing';
import {getChatNameRenderInfo} from '../ChatNameUtil';
import type {SidepanelChat} from '../ChatRouter';
import {type ChatSessionStarUpdate, useChatSessionMenuOptions} from '../ChatSessionMenu';
import {updateChatSessionInCache} from '../RenameChatModal';
import SidebarRow from './SidebarRow';

const useStyles = createStyles((_, {showDots}: {showDots: boolean}) => ({
  dots: {
    visibility: showDots ? 'visible' : 'hidden',
  },
}));

export interface RedesignChatRowProps {
  item: ChatSidepanelItem;
  onStarUpdated: (update: ChatSessionStarUpdate) => Promise<void>;
  onSelect: (chat: SidepanelChat) => void;
  isSelected: boolean;
}

/**
 * A single Starred/Recent row in the redesigned chat sidebar. Built on the shared {@link SidebarRow}
 * so it aligns with the nav items, with a dimmed icon. There is no inline star — starring lives in
 * the hover overflow ("…") menu alongside Rename, matching the mock.
 */
export default function RedesignChatRow({
  item,
  onStarUpdated,
  onSelect,
  isSelected,
}: RedesignChatRowProps): JSX.Element {
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {classes} = useStyles({showDots: item.id != null && (isHovering || isMenuOpen)});
  const apolloClient = useApolloClient();
  const chatNameInfo = getChatNameRenderInfo(item);

  const menuOptions = useChatSessionMenuOptions({
    chatSession: {
      id: item.id ?? '',
      name: item.name,
      star: item.star,
    },
    onStarUpdated,
  });

  const onChatNameUpdated = useCallback(
    (message: {data: {name: string; modifiedAt: string}}) => {
      if (item.id != null) {
        updateChatSessionInCache(apolloClient, item.id, message.data.name, message.data.modifiedAt);
      }
    },
    [apolloClient, item.id]
  );

  useRealtimeChannelSubscription(
    item.id != null ? getRealtimeChannelNameForApiId(item.id) : '',
    onChatNameUpdated,
    [RealtimeMessageType.CHAT_NAME_UPDATED]
  );

  const dots =
    item.id != null ? (
      <span
        className={classes.dots}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <OverflowMenu
          buttonProps={{'aria-label': 'Chat options'}}
          isIconHorizontal={false}
          isOpen={isMenuOpen}
          menuProps={{dropLeft: true, width: MenuWidth.FIT_CONTENT}}
          onOpenChange={setIsMenuOpen}
          options={menuOptions}
        />
      </span>
    ) : undefined;

  return (
    <SidebarRow
      icon={<ChatHistoryItemIcon item={item} />}
      label={chatNameInfo.node}
      tooltipLabel={chatNameInfo.text}
      dimIcon={true}
      active={isSelected}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      onClick={() => {
        onSelect(item.id != null ? {id: item.id, type: item.type} : {id: null});
      }}
      trailing={dots}
      dataTestAttrs={{
        component: 'RedesignChatRow',
        element: 'root',
        key: item.name ?? '',
        ...(isSelected ? {index: 'active'} : {}),
      }}
    />
  );
}
